import { useState, useEffect } from 'react';
import { Save, Printer, AlertCircle, CheckCircle } from 'lucide-react';
import { posSettings } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';

const STATIONS = ['cashier', 'kitchen', 'bar', 'pastry'];

const DEFAULTS = {
  header: { businessName: '', address: '', contact: '', vatRegTin: '', footer: 'Thank you for dining with us!' },
  printers: {
    cashier: { ip: '', port: 9100, enabled: true },
    kitchen: { ip: '', port: 9100, enabled: true },
    bar:     { ip: '', port: 9100, enabled: false },
    pastry:  { ip: '', port: 9100, enabled: false },
  },
};

function merge(target, source) {
  if (!source) return target;
  const result = { ...target };
  for (const key of Object.keys(target)) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
      result[key] = typeof target[key] === 'object' && !Array.isArray(target[key])
        ? merge(target[key], source[key])
        : source[key];
    }
  }
  return result;
}

export default function PrinterSettingsPage() {
  const { posToken } = usePosStore();
  const [form, setForm] = useState(DEFAULTS);
  const [effective, setEffective] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await posSettings.get(posToken);
        if (res.data.settings) setForm(merge(DEFAULTS, res.data.settings));
        setEffective(res.data.effective);
      } catch { /* ignore on first load */ }
    })();
  }, [posToken]);

  const setHeader = (key, val) =>
    setForm(f => ({ ...f, header: { ...f.header, [key]: val } }));

  const setPrinter = (station, key, val) =>
    setForm(f => ({
      ...f,
      printers: { ...f.printers, [station]: { ...f.printers[station], [key]: val } },
    }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await posSettings.save(posToken, { header: form.header, printers: form.printers });
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestPrint = async () => {
    setTesting(true);
    setError(null);
    try {
      await posSettings.test(posToken);
      setSuccess('Test page sent to cashier printer');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Test print failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 max-w-2xl mx-auto">
      <h1 className="font-bold text-gray-900 text-lg mb-5">Printer Settings</h1>

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 mb-4">
          <CheckCircle className="h-4 w-4 flex-shrink-0" /> {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Receipt Header */}
      <section className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <h2 className="font-semibold text-gray-900 text-sm mb-4">Receipt Header</h2>
        <div className="space-y-3">
          {[
            { key: 'businessName', label: 'Business Name' },
            { key: 'address',      label: 'Address' },
            { key: 'contact',      label: 'Contact' },
            { key: 'vatRegTin',    label: 'VAT Reg TIN' },
            { key: 'footer',       label: 'Footer Message' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.header[key]}
                onChange={e => setHeader(key, e.target.value)}
                placeholder={effective?.header?.[key] ? `From env: ${effective.header[key]}` : ''}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Printers */}
      <section className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <h2 className="font-semibold text-gray-900 text-sm mb-4">Printers</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-200">
                <th className="text-left pb-2 w-20">Station</th>
                <th className="text-left pb-2">IP Address</th>
                <th className="text-left pb-2 w-20 pl-2">Port</th>
                <th className="text-center pb-2 w-20">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {STATIONS.map(station => {
                const cfg = form.printers[station];
                const envIp = effective?.printers?.[station]?.ip;
                return (
                  <tr key={station} className="border-b border-gray-100">
                    <td className="py-2 font-medium text-gray-700 capitalize">{station}</td>
                    <td className="py-2 pr-2">
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={cfg.ip}
                        onChange={e => setPrinter(station, 'ip', e.target.value)}
                        placeholder={envIp || '192.168.x.x'}
                      />
                    </td>
                    <td className="py-2 pl-2 pr-2">
                      <input
                        type="number"
                        className="w-16 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={cfg.port}
                        onChange={e => setPrinter(station, 'port', parseInt(e.target.value) || 9100)}
                      />
                    </td>
                    <td className="py-2 text-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-blue-600"
                        checked={cfg.enabled}
                        onChange={e => setPrinter(station, 'enabled', e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleTestPrint}
          disabled={testing || saving}
          className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Printer className="h-4 w-4" />
          {testing ? 'Sending…' : 'Test Print — Cashier'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving || testing}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 ml-auto"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
